import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Calendar, ClipboardCheck, Target, AlertTriangle, TrendingUp,
  Copy, Share2, CheckCircle2, Loader2, Coffee,
} from 'lucide-react';
import { toast } from 'sonner';
import PullToRefresh from 'react-simple-pull-to-refresh';
import { useFM } from '@/context/FMContext';
import { axiosInstance } from '@/App';

/**
 * Icon map for huddle section titles.
 */
const SECTION_ICONS = {
  'Trials Scheduled Today': Calendar,
  'Compliance Due Today': ClipboardCheck,
  'Trainer Targets': Target,
  'Unresolved Issues': AlertTriangle,
  'Pipeline Follow-ups': TrendingUp,
};

/**
 * Get the icon component for a section title.
 * Falls back to ClipboardCheck for unknown titles.
 */
function getSectionIcon(title) {
  return SECTION_ICONS[title] || ClipboardCheck;
}

/**
 * Check if a section title should have a red tint when items are present.
 */
function isAlertSection(title) {
  return title === 'Unresolved Issues';
}

/**
 * FMHuddle - Morning Huddle Agenda Page.
 * Auto-generated huddle agenda with 5 sections from today's data.
 * Copy to clipboard and WhatsApp share buttons for sending to trainer group.
 */
export default function FMHuddle() {
  const { selectedGymId, selectedGym } = useFM();
  const navigate = useNavigate();
  const [huddle, setHuddle] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [copied, setCopied] = useState(false);

  const fetchHuddle = useCallback(async () => {
    if (!selectedGymId) return;
    setLoading(true);
    setError(null);
    try {
      const res = await axiosInstance.get(`/fm/huddle/${selectedGymId}`);
      setHuddle(res.data);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to load huddle agenda');
    } finally {
      setLoading(false);
    }
  }, [selectedGymId]);

  useEffect(() => {
    fetchHuddle();
  }, [fetchHuddle]);

  const handleRefresh = async () => {
    await fetchHuddle();
  };

  const handleCopy = async () => {
    if (!huddle?.shareable_text) return;
    try {
      await navigator.clipboard.writeText(huddle.shareable_text);
      setCopied(true);
      toast.success('Agenda copied!');
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error('Failed to copy');
    }
  };

  const handleWhatsApp = () => {
    if (!huddle?.shareable_text) return;
    const url = `https://wa.me/?text=${encodeURIComponent(huddle.shareable_text)}`;
    window.open(url, '_blank');
  };

  // Format today's date
  const todayFormatted = new Date().toLocaleDateString('en-IN', {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    timeZone: 'Asia/Kolkata',
  });

  // No gym selected
  if (!selectedGymId) {
    return (
      <div className="flex flex-col items-center justify-center py-16 text-center">
        <Coffee className="w-12 h-12 text-slate-300 mb-3" />
        <p className="text-slate-500 text-sm">Select a gym to view huddle agenda</p>
      </div>
    );
  }

  // Loading skeleton
  if (loading) {
    return (
      <div className="space-y-4">
        {/* Header skeleton */}
        <div className="bg-white rounded-lg border p-4 animate-pulse">
          <div className="h-5 bg-slate-200 rounded w-3/4 mb-2" />
          <div className="h-3 bg-slate-100 rounded w-1/2 mb-2" />
          <div className="h-3 bg-slate-100 rounded w-1/4" />
        </div>
        {/* Section skeletons */}
        {[1, 2, 3, 4, 5].map((i) => (
          <div key={i} className="bg-white rounded-lg border p-4 animate-pulse">
            <div className="h-4 bg-slate-200 rounded w-2/5 mb-3" />
            <div className="h-3 bg-slate-100 rounded w-full mb-2" />
            <div className="h-3 bg-slate-100 rounded w-4/5" />
          </div>
        ))}
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-center">
        <p className="text-red-700 text-sm">{error}</p>
        <button onClick={fetchHuddle} className="mt-2 text-sm text-red-600 underline">
          Retry
        </button>
      </div>
    );
  }

  const sections = huddle?.sections || [];
  const allEmpty = sections.every((s) => (s.items || []).length === 0);
  const gymName = huddle?.gym_name || selectedGym?.name || selectedGym?.gym_name || 'Gym';

  return (
    <PullToRefresh
      onRefresh={handleRefresh}
      pullingContent=""
      refreshingContent={
        <div className="text-center py-2 text-sm text-slate-500">Refreshing...</div>
      }
    >
      <div className="space-y-4 pb-20">
        {/* Header */}
        <div className="bg-white rounded-xl border p-4 shadow-sm">
          <h2 className="text-lg font-bold text-slate-800">
            {gymName} - Morning Huddle
          </h2>
          <p className="text-sm text-slate-500 mt-1">{todayFormatted}</p>
        </div>

        {/* Empty state */}
        {allEmpty && (
          <div className="bg-green-50 border border-green-200 rounded-xl p-6 text-center">
            <CheckCircle2 className="w-10 h-10 text-green-500 mx-auto mb-2" />
            <p className="text-sm font-medium text-green-700">
              No agenda items for today. All caught up!
            </p>
          </div>
        )}

        {/* Agenda sections */}
        {sections.map((section, idx) => {
          const Icon = getSectionIcon(section.title);
          const items = section.items || [];
          const hasItems = items.length > 0;
          const isAlert = isAlertSection(section.title) && hasItems;

          return (
            <div
              key={idx}
              className={`rounded-lg border p-4 bg-white shadow-sm mb-3 ${
                isAlert ? 'border-red-200' : ''
              }`}
            >
              {/* Section title */}
              <div className="flex items-center gap-2 mb-3">
                <Icon
                  className={`w-4 h-4 flex-shrink-0 ${
                    isAlert ? 'text-red-500' : 'text-slate-500'
                  }`}
                />
                <h3
                  className={`text-sm font-semibold ${
                    isAlert ? 'text-red-700' : 'text-slate-800'
                  }`}
                >
                  {section.title}
                  {section.count > 0 && (
                    <span className="ml-1.5 text-xs font-normal text-slate-400">
                      ({section.count})
                    </span>
                  )}
                </h3>
              </div>

              {/* Items */}
              {hasItems ? (
                <ul className="space-y-1.5">
                  {items.map((item, i) => (
                    <li key={i} className="flex items-start gap-2 text-sm text-slate-600">
                      <span className="text-slate-400 mt-0.5 flex-shrink-0">&#8226;</span>
                      <span>{item.text}</span>
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="text-sm text-slate-400 italic">No items</p>
              )}
            </div>
          );
        })}

        {/* Share buttons */}
        <div className="flex gap-3">
          {/* Copy to Clipboard */}
          <button
            onClick={handleCopy}
            className={`flex-1 h-12 rounded-lg border font-medium text-sm flex items-center justify-center gap-2 active:scale-[0.97] transition-all ${
              copied
                ? 'bg-green-50 border-green-300 text-green-700'
                : 'bg-white border-slate-200 text-slate-700 hover:bg-slate-50'
            }`}
          >
            {copied ? (
              <>
                <CheckCircle2 className="w-4 h-4" />
                Copied!
              </>
            ) : (
              <>
                <Copy className="w-4 h-4" />
                Copy Agenda
              </>
            )}
          </button>

          {/* Share to WhatsApp */}
          <button
            onClick={handleWhatsApp}
            className="flex-1 h-12 rounded-lg bg-green-500 hover:bg-green-600 text-white font-medium text-sm flex items-center justify-center gap-2 active:scale-[0.97] transition-all"
          >
            <Share2 className="w-4 h-4" />
            Share
          </button>
        </div>
      </div>
    </PullToRefresh>
  );
}
